<script setup>
import Panel from "primevue/panel";
import { computed, ref } from "vue";
import PriceBreakdownModal from "@/components/MyStudentDetail/PriceBreakdown";
import { hasTodoAddPermission } from "@/composables/authorization.js";
import { useGetOrderPricing } from "../../composables/useOrderPrice";
import { ORDER_STATUS } from "../../constants";
import { formatDateTo2Digit, parseAndFormatDateStringToDate } from "../../util/date";
import CallToActionIcon from "../shared/icons/CallToActionIcon.vue";
import DynamicSection from "./DynamicSection.vue";
import FilterableTodosSection from "./FilterableTodosSection/index.vue";

const props = defineProps({
  programSession: {
    type: Object,
    required: true,
  },
  todos: {
    type: Array,
    required: true,
  },
  todosLoading: {
    type: Boolean,
    default: false,
  },
  todosSectionEnabled: {
    type: Boolean,
    required: true,
  },
  canViewSubmissions: {
    type: Boolean,
    default: false,
  },
  studentOktaId: {
    type: String,
    default: undefined,
  },
  applicationId: {
    type: String,
    default: undefined,
  },
  orientations: {
    type: Array,
    required: true,
    default: () => [],
  },
  dataLoaded: {
    type: Boolean,
    required: true,
  },
});

const collapsed = ref(true);

const openPriceBreakdownModal = ref(false);

const priceBreakDownAvailable = computed(
  () => ![ORDER_STATUS.draft, ORDER_STATUS.withdrawn].includes(props.programSession?.order?.status),
);

const todosSectionVisible = computed(
  () => props.todosSectionEnabled && props.programSession?.order?.status !== ORDER_STATUS.draft,
);

const displayPriceBreakDown = computed(() => props.programSession?.displayStartingPrice);

const { isLoadingPriceBreakdown, priceBreakDownData } = useGetOrderPricing({
  priceBreakDownAllowed: priceBreakDownAvailable.value && displayPriceBreakDown.value,
  sessionId: props.programSession?.programId,
  homeInstitutionId: props.programSession?.order?.homeInstitutionId,
  order: props.programSession?.order,
});

const onToggle = (event) => {
  collapsed.value = event.value;
};

const orientationColumns = [
  { label: "Orientation Name", key: "orientationName" },
  { label: "Module Name", key: "moduleName" },
  { label: "Module Status", key: "moduleStatus" },
];
</script>
<template>
  <Panel
    toggleable
    :collapsed="collapsed"
    :pt="{
      root: 'w-full bg-white border-t-8 border-purple-700 rounded-lg shadow-md',
      header: 'flex items-center justify-between border-none border-0 p-4',
      content: 'border-none border-0 pb-6',
    }"
    @toggle="onToggle"
  >
    <template #header>
      <div class="flex flex-col w-full">
        <div class="font-semibold text-sm text-indigo-base">
          <PriceBreakdownModal
            v-show="openPriceBreakdownModal"
            :is-loading-price-breakdown="isLoadingPriceBreakdown"
            :session="props.programSession ?? {}"
            :price="priceBreakDownData ?? {}"
            :open-modal="openPriceBreakdownModal"
            @close-modal="() => (openPriceBreakdownModal = false)"
          />
        </div>
        <span class="font-bold text-base text-cyan-200">
          <router-link
            v-if="props.programSession?.programUrl"
            :to="props.programSession?.programUrl"
            target="_blank"
            rel="noopener"
          >
            {{ props.programSession?.program }}
          </router-link>
          <span v-else>{{ props.programSession?.program }}</span>
        </span>
        <div class="grid md:grid-rows-2 md:grid-flow-col mt-2 gap-x-4 gap-y-1 font-semibold text-sm text-indigo-base">
          <span>
            Status:
            <span class="!font-normal">{{ props.programSession?.status }}</span></span
          >
          <div v-if="displayPriceBreakDown" class="flex-1 flex-row hidden md:flex">
            Price breakdown:
            <div v-if="priceBreakDownAvailable" class="flex flex-row ml-2 items-center">
              <div
                class="flex flex-row items-center cursor-pointer text-cyan-200"
                data-testid="my-students-price-breakdown-view"
                @click="() => (openPriceBreakdownModal = true)"
              >
                <span>VIEW</span><CallToActionIcon class="ml-2" />
              </div>
            </div>
            <div v-else class="ml-1 !font-normal">Not Available</div>
          </div>
          <span v-if="priceBreakDownData?.session_start_date && priceBreakDownData?.session_end_date">
            Program Dates:
            <span class="!font-normal"
              >{{
                parseAndFormatDateStringToDate(priceBreakDownData?.session_start_date, "yyyy-MM-dd", "MM/dd/yyyy") +
                " - " +
                parseAndFormatDateStringToDate(priceBreakDownData?.session_end_date, "yyyy-MM-dd", "MM/dd/yyyy")
              }}
            </span></span
          >
          <span>
            Created Date:
            <span class="!font-normal">{{ formatDateTo2Digit(props.programSession?.createdDate) }} </span></span
          >
          <div v-if="displayPriceBreakDown || priceBreakDownAvailable" class="flex flex-1 flex-row md:hidden mt-2">
            <div
              class="flex flex-row items-center cursor-pointer underline decoration-solid underline-offset-1 text-cyan-200"
              data-testid="my-students-price-breakdown-view"
              @click="() => (openPriceBreakdownModal = true)"
            >
              Student Price Breakdown
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #togglericon>
      <span
        class="material-symbols-outlined text-2xl leading-none text-blue-darker transition-transform duration-150"
        :class="{ 'rotate-180': !collapsed }"
      >expand_circle_down</span>
    </template>
    <div class="flex flex-col m-0 w-full">
      <hr v-if="!collapsed" class="border-t border-gray-200 dark:border-gray-700 mb-6" />
      <FilterableTodosSection
        v-if="todosSectionVisible && (props.todos.length > 0 || props.todosLoading || hasTodoAddPermission)"
        :todos="props.todos"
        :loading="props.todosLoading"
        :can-view-submissions="props.canViewSubmissions"
        :student-okta-id="props.studentOktaId"
        :application-id="props.applicationId"
      />
      <DynamicSection
        v-if="props.orientations.length"
        class="pt-6 px-6"
        title="Orientation"
        :rows="props.orientations"
        :columns="orientationColumns"
        data-test-id="student-detail-orientations-section"
      />
    </div>
  </Panel>
</template>
